import { useState, useEffect, useRef } from 'react';

// Match-only autocomplete over a preloaded catalog list ([{id, name}]). Free
// typing is always propagated via onChange(name, null); picking a suggestion
// propagates onChange(name, id). Applicants can't create catalog rows, so there
// is no "create new" option — an unmatched value simply has a null id.
//
// `strict` forbids free-text values: on blur an entry that doesn't exactly match
// a catalog option is discarded (the field is cleared). Use it where only a real
// catalog row is acceptable (e.g. skills).
const CatalogAutocomplete = ({ value, options = [], onChange, placeholder, className, ariaLabel, disabled, strict = false }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef(null);
  const selectingRef = useRef(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const term = inputValue.trim().toLowerCase();
  const matches = term
    ? options.filter((option) => (option.name || '').toLowerCase().includes(term)).slice(0, 25)
    : [];

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setShowResults(true);
    // Free text keeps the field editable but has no catalog id until a pick.
    onChange(newValue, null);
  };

  const handleSelect = (option) => {
    selectingRef.current = true;
    setInputValue(option.name);
    setShowResults(false);
    onChange(option.name, option.id);
  };

  // In strict mode, only a value that exactly matches a catalog option may stay.
  // Runs after any pending suggestion click has had a chance to commit.
  const handleBlur = () => {
    if (!strict) return;
    setTimeout(() => {
      if (selectingRef.current) { selectingRef.current = false; return; }
      const hit = options.find((o) => (o.name || '').trim().toLowerCase() === inputValue.trim().toLowerCase());
      if (hit) {
        setInputValue(hit.name);
        onChange(hit.name, hit.id);
      } else if (inputValue) {
        setInputValue('');
        onChange('', null);
      }
    }, 120);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        aria-label={ariaLabel}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowResults(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        autoComplete="off"
      />
      {showResults && matches.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg">
          {matches.map((option) => (
            <div
              key={option.id}
              onMouseDown={() => { selectingRef.current = true; }}
              onClick={() => handleSelect(option)}
              className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
            >
              {option.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogAutocomplete;
